<template>
  <a-form-item :name="name" :required="required">
    <template v-if="$slots['label'] || label" #label>
      <slot name="label" />
      <div class="flex-gap-4" v-if="label || showTooltip">
        <span v-if="label">
          {{ label }}
        </span>
        <a-tooltip :title="tooltip" v-if="showTooltip">
          <InfoCircleOutlined class="info-icon" /> 
          <span v-if="optional" class="help"> (opcional) </span>
        </a-tooltip>
      </div>
    </template>

    <a-select
      :getPopupContainer="(triggerNode) => triggerNode.parentNode"
      :value="value"
      class="select"
      :placeholder="placeholder"
      @update:value="handleChange"
      @change="emit('change')"
      :loading="loading"
      :mode="multiple ? 'multiple' : null"
			:disabled="disabled"
      :filter-option="filterOption"
      :allowClear="allowClear"
    >
      <a-select-option v-for="item in items" :key="item.id" :value="item.id" :title="item.title">
        {{ item.title }}
      </a-select-option>

      <template #notFoundContent>
				<div class="dropdown-default__wrapper">
					Aún no hay respuestas rápidas automatizadas.
					<br/>
					Crea una desde el módulo de Ajustes de la plataforma.
				</div>
      </template>
    </a-select>

    <p v-if="help" class="help">{{ help }}</p>
  </a-form-item>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { intersection } from "lodash";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

import { quickAnswersService } from "@/app/platformSettings/services/QuickActionsService";

const props = defineProps({
  value: {
    required: false,
    type: [String, Number, Array],
  },
  required: {
    type: Boolean,
    default: false,
  },
  label: {
    required: false,
    type: String,
    default: "",
  },
	queue: {
    required: false,
    type: [Number, null, Array],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: "Esta respuesta será enviada automáticamente al terminar tu gestión.",
  },
  showTooltip: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    default: "automaticReply",
    required: false,
  },
  placeholder: {
    type: String,
    default: "Seleccionar",
  },
	replyType: { 
		type: Number, 
		default: 2 
	},
  useQueueAutomaticField: { 
    type: Boolean, 
    default: false 
  },
  queueAutomaticReplies: {
    required: false,
    type: [Number, null, Array],
  },
  help: String,
  optional: { 
    type: Boolean, 
    default: false 
  },
  allowClear: { 
    type: Boolean, 
    default: false 
  },
});

const emit = defineEmits(["update:value", "change"]);

const items = ref([]);

const handleChange = (newValue) => emit("update:value", newValue);

const loading = ref(false);

const fetchQuickReplyByQueue = async () => {
  loading.value = true;

	if (props.queue) {

    if (props.useQueueAutomaticField && props.queueAutomaticReplies) {
      items.value = props.queueAutomaticReplies.flat();

    } else {
      const quickAnswersData = await quickAnswersService.getList({
        queue: props.queue,
        reply_type: props.replyType,
      });
      items.value = quickAnswersData.results.flat();
    };

		const validQueuesId = intersection(
			[...items.value.map((item) => item.id), ""],
			props.multiple ? props.value : [props.value]
		);
	
		const newValue = props.multiple ? validQueuesId : validQueuesId[0];
		emit("update:value", validQueuesId.length ? newValue : undefined);
	
		loading.value = false;
	}

};

const valueExistsInItems = computed(() => items.value.some((i) => i.id === props.value));

const filterOption = (input, option) => {
  const row = items.value.find((i) => i.id === option.value);
  return row?.title?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0;
};

watch(
  () => props.queue,
  () => {
		fetchQuickReplyByQueue()
	},
  { immediate: true }
);

defineExpose({ valueExistsInItems });
</script>

<style lang="sass" scoped>
.select
  width: 100%

.help
  color: $text-tertiary

:deep(.ant-select-loading .ant-select-arrow-loading)
  left: 10px

:deep(.ant-select-loading .ant-select-selection-overflow)
  opacity: 0

.flex-gap-4
  display: flex
  gap: 4px

.dropdown-default__wrapper
  margin: 20px auto
  // width: 190px
  text-align: center
  color: $gray-8
  font-size: 12px
</style>
